import React, { useContext, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { Company } from '_lib/api/apiTypes.shim';

import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { RecoverableErrorState, ManualErrorBoundary } from '_lib/components/ManualErrorBoundary/ManualErrorBoundary';
import { PagedList } from '_lib/featureComponents/PagedList/PagedList';

import { PhoneCallListItem } from '../../callPop/common/CustomerPane/PhoneCallListItem/PhoneCallListItem';

import { UserDisplayDTO } from './CustomerPhoneCallHistory.types';

interface CustomerPhoneCallHistoryProps {
  providerCustomerId?: string;
  customerId?: string;
  company: Company;
}

export const CustomerPhoneCallHistory = (props: CustomerPhoneCallHistoryProps): React.ReactElement => {
  const { providerCustomerId, customerId, company } = props;

  /* Injected Provider stuff */
  const api = useContext(ApiContext);

  /* Local Component State */
  const [recoverableError, setRecoverableError] = useState<RecoverableErrorState | null>(null);

  /* Queries */
  const callCenterUsersQuery = api.users.useAllCallCenterUsersCached({
    params: { callCenterId: company.callCenterId }
  });

  const accountUsersQuery = api.accountUsers.useAllAccountUsersCached({
    params: { callCenterId: company.callCenterId, accountId: company.accountId }
  });

  const phoneCallQuery = api.phoneCallsV1.useCustomerCallHistoryCached({
    params: { companyId: !customerId ? company.id : undefined, customerProviderId: providerCustomerId, customerId }
  });

  /* Error handling */
  if (callCenterUsersQuery.error && !recoverableError)
    setRecoverableError({
      error: callCenterUsersQuery.error,
      message: 'Failed to load Call Center Users'
    });

  if (accountUsersQuery.error && !recoverableError)
    setRecoverableError({
      error: accountUsersQuery.error,
      message: 'Failed to load Account Users'
    });

  if (phoneCallQuery.error && !recoverableError)
    setRecoverableError({
      error: phoneCallQuery.error,
      message: 'Failed to load Phone Calls'
    });

  if (recoverableError) {
    return (
      <ManualErrorBoundary
        errorState={recoverableError}
        reportingCtx={{ extra: { providerCustomerId, company } }}
        onRefresh={async () => {
          if (callCenterUsersQuery.error) await callCenterUsersQuery.refetch();
          if (accountUsersQuery.error) await accountUsersQuery.refetch();
          if (phoneCallQuery.error) await phoneCallQuery.refetch();

          setRecoverableError(null);
        }}
      />
    );
  }

  /* Data transformations */
  const users: UserDisplayDTO[] = (callCenterUsersQuery.data || [])
    .concat(accountUsersQuery.data || [])
    .map<UserDisplayDTO>((u) => ({
      id: u.id,
      firstName: u.firstName,
      lastName: u.lastName,
      isActive: u.isActive
    }));

  const isFetching = callCenterUsersQuery.isFetching || accountUsersQuery.isFetching || phoneCallQuery.isFetching;

  /* Rendering */
  return (
    <FlexBox sx={{ px: 3, py: 2, flexDirection: 'column', width: '100%' }}>
      {isFetching && <BackdropSpinner open={true} />}
      <PagedList
        queryResult={phoneCallQuery}
        emptyListMessage='No Phone Calls were found.'
        renderFunction={(item) => (
          <PhoneCallListItem phoneCall={item} callCenterUsers={users} company={company} key={item.id} />
        )}
      />
    </FlexBox>
  );
};
